// Shared plan data + checkout actions for both the marketing /pricing page and
// the in-app /app/upgrade page, so the two never drift. The marketing page sells
// to logged-out visitors; the in-app page lets a signed-in free user upgrade
// WITHOUT being ejected to marketing. Same prices, same Stripe checkout.

export type PlanTier = {
  name: string;
  blurb: string;
  monthly: number; // USD / month, billed monthly
  yearlyTotal: number; // USD billed once per year ("2 months free" = monthly × 10)
  cta: string;
  /** "free" tiers link directly (marketing); paid tiers use startCheckout() */
  href?: string;
  tierKey?: "writer" | "exam" | "family";
  featured?: boolean;
  features: string[];
};

// Invented pricing — USD. Yearly billing is "2 months free": the annual charge
// is 10× the monthly rate, and the card shows the per-month equivalent.
export const TIERS: PlanTier[] = [
  {
    name: "Free",
    blurb: "Get a feel for it.",
    monthly: 0,
    yearlyTotal: 0,
    cta: "Start free",
    href: "/auth/signup",
    features: [
      "Every technique, every trait",
      "30 AI mark-ups per month",
      "Progress saved on one device",
    ],
  },
  {
    name: "Writer",
    blurb: "For one writer, all in.",
    monthly: 14,
    yearlyTotal: 140,
    cta: "Choose Writer",
    tierKey: "writer",
    featured: true,
    features: [
      "Every technique, every trait",
      "150 AI mark-ups per month",
      "All difficulty levels & pass gates",
      "Checkpoints, certificates & history",
    ],
  },
  {
    name: "Exam Prep",
    blurb: "Serious about an exam.",
    monthly: 29,
    yearlyTotal: 290,
    cta: "Choose Exam Prep",
    tierKey: "exam",
    features: [
      "Everything in Writer",
      "All exam modules: Selective, NAPLAN, IELTS, TOEFL, Cambridge",
      "Full timed mock tests",
      "Detailed marked reports",
      "Mentor-text library & priority coaching",
    ],
  },
  {
    name: "Family",
    blurb: "One household: up to 3 children, plus your own profile free.",
    monthly: 44,
    yearlyTotal: 440,
    cta: "Start Family",
    tierKey: "family",
    features: [
      "Up to 3 child profiles + you",
      "Exam access for every learner",
      "Parent progress dashboard",
      "Kid sign-in with username + PIN, no email needed",
    ],
  },
];

/**
 * Kick off Stripe Checkout for a paid tier. `from` controls where Stripe sends
 * the user if they cancel: from the in-app upgrade page we return them to the
 * app (not the marketing site). Existing subscribers are routed to the billing
 * portal by the API.
 */
export async function startCheckout(
  tier: "writer" | "exam" | "family",
  period: "monthly" | "yearly",
  from?: "app",
) {
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tier, period, from }),
  });
  if (res.status === 401) {
    // Deliberate full-document navigation, not a router push.
    // The session just changed, so the next render must come from the server
    // with fresh auth rather than a cached client tree.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- session changed — needs a server render
    window.location.href = `/auth/signup?plan=${tier}`;
    return;
  }
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  else alert(data.error ?? "Could not start checkout.");
}

export async function openPortal() {
  const res = await fetch("/api/billing/portal", { method: "POST" });
  if (res.status === 400 || res.status === 401) {
    // Deliberate full-document navigation, not a router push.
    // The session just changed, so the next render must come from the server
    // with fresh auth rather than a cached client tree.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- session changed — needs a server render
    window.location.href = "/auth/login";
    return;
  }
  const data = await res.json();
  if (data.url) window.location.href = data.url;
  else alert(data.error ?? "Could not open billing portal.");
}
